import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../frontend/context/AuthContext";
import "./Order.css";

const API_BASE = process.env.REACT_APP_API_BASE ;

export default function Orders() {
  const { isAuthenticated } = useAuth();

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [approving, setApproving] = useState(false);
  const [paidAmount, setPaidAmount] = useState(""); // New state for input
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const PAGE_SIZE = 5; // Adjust as you like
  const [successPopup, setSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const getAuthToken = () =>
    localStorage.getItem("access") || localStorage.getItem("access_token");

  // Fetch Orders (paginated)
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const token = getAuthToken();
        if (!token) {
          setError("Please login again.");
          setLoading(false);
          return;
        }

        const res = await axios.get(
          `${API_BASE}/orders/pending/?page=${currentPage}&page_size=${PAGE_SIZE}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const data = res.data.results || res.data;

        // Use backend pagination if available
        setTotalPages(
          res.data.total_pages ||
            res.data.num_pages ||
            Math.ceil((res.data.count || data.length) / PAGE_SIZE),
        );

        const formatted = data.map((order) => ({
          ...order,
          customer_name: order.user?.username || "Unknown User",
          customer_phone: order.user?.phone || "Not Provided",
          items: order.items || [],
          payment_screenshot: order.payment_screenshot,
        }));

        setOrders(formatted);
      } catch (err) {
        console.error("FETCH ERROR:", err.response?.data);
        setError("Failed to load orders.");
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated, currentPage]);

  const calculateTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((total, item) => {
      const price = item.product?.price || item.price || 0;
      return total + price * (item.quantity || 0);
    }, 0);
  };

  // Approve Order
 const handleApprove = async () => {
  if (!selectedOrder) return;

  try {
    setApproving(true);
    const token = getAuthToken();

    await axios.patch(
      `${API_BASE}/orders/${selectedOrder.id}/update-status/`,
      { status: "approved", paid_amount: paidAmount },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    setOrders((prev) =>
      prev.filter((o) => o.id !== selectedOrder.id)
    );

    setSelectedOrder(null);
    setPaidAmount("");

    // ✅ Show beautiful popup instead of alert
    setPopupMessage("Order Approved Successfully!");
    setSuccessPopup(true);

  } catch (err) {
    console.error("APPROVE ERROR:", err.response?.data);

    setPopupMessage(
      err.response?.data?.detail || "Failed to approve order."
    );
    setSuccessPopup(true);

  } finally {
    setApproving(false);
  }
};

  if (loading) return <div>Loading orders...</div>;
  if (error) return <div>{error}</div>;

  const paginatedOrders = orders; // Already paginated by the backend!

  return (
    <div className="orders-container">
      <h2>Pending Orders</h2>

      {paginatedOrders.length === 0 ? (
        <p>No pending orders.</p>
      ) : (
        <>
          <table className="orders-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Phone</th>
                <th>Date</th>
                <th>Total (ETB)</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>{order.customer_name}</td>
                  <td>{order.customer_phone}</td>
                  <td>{new Date(order.created_at).toLocaleDateString()}</td>
                  <td>{calculateTotal(order.items).toFixed(2)}</td>
                  <td>
                    <button
                      onClick={() => {
                        setSelectedOrder(order);
                        setPaidAmount(order.paid_amount || "");
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination Controls */}
          <div className="pagination-controls" style={{ marginTop: 20 }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
            >
              Previous
            </button>
            <span style={{ padding: "0 1em" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* DETAILS MODAL */}
      {selectedOrder && (
        <div className="modal-overlay">
          <div className="order-details-modal">
            <h3>Order #{selectedOrder.id}</h3>
            <button onClick={() => setSelectedOrder(null)} className="back">
              ×
            </button>

            <p>
              <strong>Username:</strong> {selectedOrder.customer_name}
            </p>
            <p>
              <strong>Phone:</strong> {selectedOrder.customer_phone}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {new Date(selectedOrder.created_at).toLocaleString()}
            </p>

            {/* Payment Screenshot */}
            {selectedOrder.payment_screenshot && (
              <div style={{ margin: "10px 0" }}>
                <strong>Payment Screenshot:</strong>
                <div>
                  <img
                    src={
                      selectedOrder.payment_screenshot.startsWith("http")
                        ? selectedOrder.payment_screenshot
                        : `${API_BASE.replace("/api/v1", "")}${selectedOrder.payment_screenshot}`
                    }
                    alt="Payment Screenshot"
                    className="order-payment-screenshot"
                    style={{ maxWidth: 300, maxHeight: 300 }}
                  />
                </div>
              </div>
            )}

            <h4>Ordered Items</h4>
            {selectedOrder.items.length === 0 ? (
              <p>No items found.</p>
            ) : (
              selectedOrder.items.map((item, index) => (
                <div key={index} className="order-product-detail">
                  <p>
                    <strong>Product:</strong> {item.product?.name}
                  </p>
                  <p>
                    <strong>Description:</strong> {item.product?.description}
                  </p>
                  <p>
                    <strong>Price:</strong> ETB {item.product?.price}
                  </p>
                  <p>
                    <strong>Quantity:</strong> {item.quantity}
                  </p>
                  {item.product?.product_images?.length > 0 && (
                    <img
                      src={
                        item.product.product_images[0].image.startsWith("http")
                          ? item.product.product_images[0].image
                          : `${API_BASE.replace("/api/v1", "")}${item.product.product_images[0].image}`
                      }
                      alt="Product"
                      style={{ maxHeight: 80, marginBottom: 5 }}
                    />
                  )}
                  <hr />
                </div>
              ))
            )}

            {/* Paid Amount Input */}
            <div style={{ margin: "1em 0" }}>
              <label>
                <strong>Paid Amount (ETB):</strong>
              </label>
              <input
                type="number"
                min="0"
                max={calculateTotal(selectedOrder.items)}
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                style={{ marginLeft: 10, width: 100 }}
              />
            </div>
            <div>
              <strong>Remaining:</strong> ETB{" "}
              {(
                calculateTotal(selectedOrder.items) -
                (parseFloat(paidAmount) || 0)
              ).toFixed(2)}
            </div>

            <h4>Total Order Value</h4>
            <p>ETB {calculateTotal(selectedOrder.items).toFixed(2)}</p>

            <button disabled={approving || !paidAmount} onClick={handleApprove}>
              {approving ? "Approving..." : "Approve Order"}
            </button>
          </div>
        </div>
      )}
      {/* SUCCESS / ERROR POPUP */}
{successPopup && (
  <div className="popup-overlay">
    <div className="popup-box success-popup">
      <div className="popup-header">
        {popupMessage.includes("Successfully") ? "Success 🎉" : "Error ❌"}
      </div>

      <div className="popup-text">
        {popupMessage}
      </div>

      <div className="popup-buttons">
        <button
          className="ok-btn"
          onClick={() => setSuccessPopup(false)}
        >
          OK
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}
